import React from "react";
import { Skeleton } from "components/ui/skeleton";

/** Grid card skeleton — mirrors the real CampaignCard grid layout exactly */
export function CampaignCardSkeleton() {
    return (
        <div className="relative overflow-hidden dark:bg-dark-800 rounded-xl border border-line-subtle dark:border-dark-700 shadow-sm fade-in">
            {/* Accent top bar */}
            <div className="absolute top-0 inset-x-0 h-[2px] loading-shimmer" />

            {/* Header */}
            <div className="px-4 pt-4 pb-3">
                <div className="flex items-start gap-3">
                    <Skeleton className="w-4 h-4 rounded flex-shrink-0 mt-1" />
                    <Skeleton className="w-2 h-2 rounded-full flex-shrink-0 mt-2" />
                    <div className="flex-1 min-w-0 space-y-1.5">
                        <Skeleton variant="text" className="!w-36" />
                        <Skeleton className="h-5 w-20 rounded-md" />
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                        <Skeleton className="w-6 h-6 rounded-lg" />
                        <Skeleton className="w-6 h-6 rounded-lg" />
                    </div>
                </div>
                <div className="flex items-center gap-1.5 mt-2.5">
                    <Skeleton className="w-3 h-3 rounded flex-shrink-0" />
                    <Skeleton variant="text" className="!w-48" />
                </div>
            </div>

            {/* Details grid */}
            <div className="px-4 py-3 border-t border-gray-50 dark:border-dark-700/60 grid grid-cols-2 gap-3">
                <div className="space-y-1">
                    <Skeleton className="h-2.5 w-14 rounded" />
                    <Skeleton variant="text" className="!w-28" />
                    <Skeleton className="h-2.5 w-10 rounded" />
                </div>
                <div className="space-y-1">
                    <Skeleton className="h-2.5 w-20 rounded" />
                    <Skeleton className="h-5 w-14 rounded" />
                </div>
            </div>

            {/* Footer */}
            <div className="px-4 py-2.5 bg-gray-50/60 dark:bg-dark-700/30 border-t border-line-subtle dark:border-dark-700">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                        <Skeleton className="w-3 h-3 rounded" />
                        <Skeleton className="h-2.5 w-20 rounded" />
                    </div>
                    <Skeleton className="w-3 h-3 rounded" />
                    <div className="flex items-center gap-1.5">
                        <Skeleton className="w-3 h-3 rounded" />
                        <Skeleton className="h-2.5 w-20 rounded" />
                    </div>
                </div>
            </div>
        </div>
    );
}

/** List row skeleton — mirrors the real CampaignCard list layout */
export function CampaignListRowSkeleton() {
    return (
        <div className="relative overflow-hidden dark:bg-dark-800 px-4 py-3 rounded-xl border border-line-subtle dark:border-dark-700 fade-in">
            <div className="absolute top-0 left-0 bottom-0 w-[2px] loading-shimmer" />
            <div className="flex items-center gap-3 pl-2">
                <Skeleton className="w-4 h-4 rounded flex-shrink-0" />
                <Skeleton className="w-2 h-2 rounded-full flex-shrink-0" />
                <div className="flex-1 min-w-0 space-y-1.5">
                    <div className="flex items-center gap-2">
                        <Skeleton variant="text" className="!w-32" />
                        <Skeleton className="h-5 w-20 rounded-md flex-shrink-0" />
                    </div>
                    <Skeleton className="h-2.5 w-44 rounded" />
                </div>
                <div className="hidden sm:block flex-shrink-0 w-32 space-y-1">
                    <Skeleton className="h-2.5 w-16 rounded" />
                    <Skeleton variant="text" className="!w-24" />
                </div>
                <div className="hidden md:block flex-shrink-0 space-y-1">
                    <Skeleton className="h-2.5 w-12 rounded" />
                    <Skeleton className="h-4 w-10 rounded" />
                </div>
                <div className="hidden lg:flex items-center gap-1.5 flex-shrink-0">
                    <Skeleton className="w-3 h-3 rounded" />
                    <Skeleton className="h-2.5 w-20 rounded" />
                    <Skeleton className="w-3 h-3 rounded" />
                    <Skeleton className="h-2.5 w-20 rounded" />
                </div>
                <Skeleton className="w-7 h-7 rounded-lg flex-shrink-0" />
            </div>
        </div>
    );
}

export function CampaignCardGridSkeleton({ count = 6, viewMode = "grid" }: { count?: number; viewMode?: string }) {
    if (viewMode === "list") {
        return (
            <div className="space-y-2" role="list" aria-label="Loading campaigns">
                {Array.from({ length: count }).map((_, i) => (
                    <CampaignListRowSkeleton key={i} />
                ))}
            </div>
        );
    }
    return (
        <div
            id="campaign-grid-skeleton"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
            role="grid"
            aria-label="Loading campaigns"
        >
            {Array.from({ length: count }).map((_, i) => (
                <CampaignCardSkeleton key={i} />
            ))}
        </div>
    );
}
